import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate, Link } from 'react-router-dom'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../common/SafeIcon'
import { useAuth } from '../../context/AuthContext'
import supabase from '../../lib/supabase'

const Signup = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [username, setUsername] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [usernameError, setUsernameError] = useState(null)
  const [checkingUsername, setCheckingUsername] = useState(false)
  const navigate = useNavigate()
  const { signUp, user } = useAuth()

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/')
    }
  }, [user, navigate])

  const checkUsernameAvailability = async (username) => {
    if (!username) return true;
    
    try {
      setCheckingUsername(true);
      const { data, error } = await supabase
        .from('profiles_sb2847x9')
        .select('id')
        .eq('username', username);

      if (error) throw error;

      if (data && data.length > 0) {
        setUsernameError('Username is already taken. Please choose a different one.');
        return false;
      } else {
        setUsernameError(null);
        return true;
      }
    } catch (err) {
      console.error('Error checking username:', err);
      setUsernameError('Error checking username availability');
      return false;
    } finally {
      setCheckingUsername(false);
    }
  };

  const handleUsernameChange = (e) => {
    const newUsername = e.target.value;
    setUsername(newUsername);

    // Debounce username check
    if (newUsername.length > 2) {
      setTimeout(() => {
        checkUsernameAvailability(newUsername);
      }, 500);
    } else {
      setUsernameError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!email || !password || !username) {
      setError('Please fill in all fields')
      return
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters long')
      return
    }
    
    if (usernameError) {
      setError('Please choose a different username')
      return
    }
    
    try {
      setLoading(true)
      setError(null)
      
      // Check username availability one more time before signup
      const isUsernameAvailable = await checkUsernameAvailability(username);
      if (!isUsernameAvailable) {
        setError('This username is already taken. Please choose a different one.');
        return;
      }
      
      console.log('Attempting to sign up...')
      await signUp({ email, password, username })
      console.log('Sign up successful, redirecting...')
      navigate('/')
    } catch (err) {
      console.error('Signup error:', err)
      
      // Provide more specific error messages
      if (err.message?.includes('duplicate key')) {
        setError('This username is already taken. Please choose a different one.');
      } else if (err.message?.includes('email')) {
        setError('This email is already registered. Please use a different email or try signing in.');
      } else {
        setError(err.message || 'Failed to create account. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-md mx-auto bg-white rounded-3xl shadow-dreamy p-8"
    >
      <h2 className="text-2xl font-display font-bold text-center text-purple-700 mb-6">
        Create Your Account
      </h2>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6">
          <p>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="username" className="block text-purple-700 font-medium mb-2">
            Username
          </label>
          <div className="relative">
            <SafeIcon
              icon={FiIcons.FiUser}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-purple-500"
            />
            <input
              type="text"
              id="username"
              value={username}
              onChange={handleUsernameChange}
              className={`w-full pl-10 pr-4 py-2 rounded-xl border ${
                usernameError ? 'border-red-400' : 'border-purple-200'
              } focus:border-purple-400 focus:ring focus:ring-purple-300 focus:ring-opacity-50`}
              placeholder="Choose a username"
              disabled={loading}
            />
            {checkingUsername && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-500"></div>
              </div>
            )}
          </div>
          {usernameError && (
            <p className="text-xs text-red-600 mt-1">{usernameError}</p>
          )}
        </div>

        <div className="mb-4">
          <label htmlFor="email" className="block text-purple-700 font-medium mb-2">
            Email
          </label>
          <div className="relative">
            <SafeIcon
              icon={FiIcons.FiMail}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-purple-500"
            />
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-xl border border-purple-200 focus:border-purple-400 focus:ring focus:ring-purple-300 focus:ring-opacity-50"
              placeholder="your.email@example.com"
              disabled={loading}
            />
          </div>
        </div>

        <div className="mb-6">
          <label htmlFor="password" className="block text-purple-700 font-medium mb-2">
            Password
          </label>
          <div className="relative">
            <SafeIcon
              icon={FiIcons.FiLock}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-purple-500"
            />
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-xl border border-purple-200 focus:border-purple-400 focus:ring focus:ring-purple-300 focus:ring-opacity-50"
              placeholder="••••••••"
              disabled={loading}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
        </div>

        <motion.button
          type="submit"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full bg-primary-500 hover:bg-primary-600 text-white py-3 px-6 rounded-xl font-medium flex items-center justify-center gap-2"
          disabled={loading || checkingUsername}
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Creating Account...
            </>
          ) : (
            <>
              <SafeIcon icon={FiIcons.FiUserPlus} className="text-lg" />
              Sign Up
            </>
          )}
        </motion.button>

        <div className="mt-6 text-center">
          <p className="text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-500 hover:text-primary-700 font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </form>
    </motion.div>
  )
}

export default Signup